"use client";

import { Box, IconButton, Skeleton, Typography, colors } from "@mui/material";
import FlexBox from "@v2/components/styled/FlexBox";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { ReactNode } from "react";

export interface SliderHeaderSettings {
    label: string | null;
    title: string;
    subtitle: string | null;
    show_accent: boolean;
    align: 'right' | 'center';
    link_text: string | null;
    link_url: string | null;
}

export default function SliderHeader({
    settings,
    showNavigation,
    onPrev,
    onNext,
    previousDisabled = false,
    nextDisabled = false,
    navigationLoading = false,
}: {
    settings: SliderHeaderSettings;
    showNavigation: boolean;
    onPrev?: () => void;
    onNext?: () => void;
    previousDisabled?: boolean;
    nextDisabled?: boolean;
    navigationLoading?: boolean;
}) {
    if (!settings.title && !settings.link_url && !showNavigation) return null;

    const controls = (
        <FlexBox sx={{ gap: 1.5, flexShrink: 0, flexDirection: 'row-reverse' }}>
            {showNavigation && (
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5 }}>
                    {navigationLoading ? (
                        <>
                            <Skeleton variant="circular" width={42} height={42} />
                            <Skeleton variant="circular" width={42} height={42} />
                        </>
                    ) : (
                        <>
                            <SliderHeaderNavButton aria-label="اسلاید قبلی" onClick={onPrev} disabled={previousDisabled}>
                                <ChevronRight size={20} />
                            </SliderHeaderNavButton>
                            <SliderHeaderNavButton aria-label="اسلاید بعدی" onClick={onNext} disabled={nextDisabled}>
                                <ChevronLeft size={20} />
                            </SliderHeaderNavButton>
                        </>
                    )}
                </Box>
            )}

            {settings.link_text && settings.link_url && (
                <Box
                    component={Link}
                    href={settings.link_url}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: '#BB202E',
                        textDecoration: 'none',
                        fontSize: 14,
                        fontWeight: 500,
                        flexShrink: 0,
                    }}
                >
                    {/* A phone has no room beside the title for a long link, so
                        it always gets the short form. */}
                    <Box component="span" sx={{ display: { sm2: 'none' } }}>مشاهده همه</Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm2: 'inline' } }}>{settings.link_text}</Box>
                </Box>
            )}
        </FlexBox>
    );

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            pb: { xs: 2, md: 3 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ...(showNavigation || (settings.link_text && settings.link_url) ? { flex: 1, minWidth: 0 } : { width: '100%' }) }}>
                {settings.title && (
                    <Box sx={{ width: 5, height: 36, bgcolor: '#BB202E', borderRadius: 1, flexShrink: 0 }} />
                )}
                {settings.title && (
                    <Typography component="h2" sx={{ fontWeight: 700, fontSize: { xs: 17, md: 24 }, color: '#242424', m: 0 }}>
                        {settings.title}
                    </Typography>
                )}
            </Box>
            {controls}
            {settings.subtitle && (
                <Typography sx={{
                    fontSize: { xs: 12, md: 13 },
                    color: 'text.secondary',
                    mt: 0,
                    width: '100%',
                    pl: settings.title ? 'calc(5px + 12px)' : 0,
                }}>
                    {settings.subtitle}
                </Typography>
            )}
        </Box>
    );
}

function SliderHeaderNavButton({ children, onClick, disabled, 'aria-label': ariaLabel }: {
    children: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    'aria-label': string;
}) {
    return (
        <IconButton
            aria-label={ariaLabel}
            onClick={onClick}
            disabled={disabled}
            sx={{
                width: 42,
                height: 42,
                border: `1px solid ${colors.grey[300]}`,
                bgcolor: '#fff',
                color: '#111',
                '&:hover': { bgcolor: colors.grey[50] },
            }}
        >
            {children}
        </IconButton>
    );
}
